import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { KnowledgeBasePageContent } from "./KnowledgeBasePageContent";
import { KnowledgeBaseSidebar, SidebarRef } from "./KnowledgeBaseSidebar";
import styles from "./KnowledgeBaseView.scss";
import { KnowledgeBaseWelcome } from "./KnowledgeBaseWelcome";
import ErrorMessage from "components/error-message/ErrorMessage";
import i18n from "components/i18n";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import * as LanguageRepository from "services/language/languageRepository";
import { KnowledgeBaseArticle, knowledgeBaseService } from "services/news/KnowledgeBaseService";
import { setLanguage } from "store/language";
import { logger } from "utils/logging";

const SUPPORTED_KNOWLEDGEBASE_LANGUAGES = ["en", "de", "ja"];

const getKnowlegdebaseLanguageCode = (locale: string): string => {
    const langCode = locale.split("_")[0] || "en";
    if (SUPPORTED_KNOWLEDGEBASE_LANGUAGES.includes(langCode)) {
        return langCode;
    }
    return "en";
};

const KnowledgeBase = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const language = useSelector((state: { languageReducer?: { lang?: { code?: string } } }) => {
        return state?.languageReducer?.lang;
    });

    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | undefined>(undefined);
    const [selectedLanguage, setSelectedLanguage] = React.useState<string>(
        getKnowlegdebaseLanguageCode(language?.code || "en")
    );
    const [selectedPage, setSelectedPage] = React.useState<KnowledgeBaseArticle | undefined>(undefined);
    const [rootPages, setRootPages] = React.useState<{ [key: string]: KnowledgeBaseArticle[] }>({});
    const [rootPageTitles, setRootPageTitles] = React.useState<{ [key: string]: string }>({});
    const [navigationHistory, setNavigationHistory] = React.useState<(KnowledgeBaseArticle | undefined)[]>([undefined]);
    const sidebarRef = React.useRef<SidebarRef>(null);

    const handleLanguageChange = (lang: string) => {
        setSelectedLanguage(lang);
        const matchingLanguage = LanguageRepository.getAllLanguages().find((l) => l.locale === lang.toUpperCase());
        if (matchingLanguage) {
            LanguageRepository.setLanguage(matchingLanguage.locale);
            dispatch(setLanguage(matchingLanguage));
            i18n.changeLanguage(matchingLanguage.code);
        }
    };

    const handleLogoClick = () => {
        setSelectedPage(undefined);
        setNavigationHistory([undefined]);
    };

    const fetchKnowledgeBase = React.useCallback(async (lang: string) => {
        try {
            setLoading(true);
            setError(undefined);

            const rootResponse = await knowledgeBaseService.fetchKnowledgeBase(lang);

            if (rootResponse?.pages) {
                setRootPages(rootResponse.pages);

                const titles: { [key: string]: string } = {};
                for (const pageId of Object.keys(rootResponse.pages)) {
                    try {
                        const pageData = await knowledgeBaseService.fetchPageById(lang, pageId);
                        titles[pageId] = pageData.title;
                    } catch {
                        titles[pageId] = t("KnowledgeBase.logoSubText");
                    }
                }
                setRootPageTitles(titles);
            }
        } catch (err) {
            logger.error(err);
            setError(err instanceof Error ? err.message : "Failed to fetch knowledge base");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        setSelectedPage(undefined);
        setNavigationHistory([undefined]);
        fetchKnowledgeBase(selectedLanguage);
    }, [selectedLanguage, fetchKnowledgeBase]);

    const handleNavigate = (page: KnowledgeBaseArticle) => {
        setNavigationHistory([...navigationHistory, selectedPage]);
        setSelectedPage(page);
    };

    const handleArticleClick = async (article: KnowledgeBaseArticle) => {
        try {
            const page = await knowledgeBaseService.fetchPageById(selectedLanguage, article.id);
            handleNavigate(page);
            await expandToShowPage(page);
        } catch (err) {
            logger.error(err);
        }
    };

    const handleBackClick = () => {
        if (navigationHistory.length > 0) {
            const previousPage = navigationHistory[navigationHistory.length - 1];
            setNavigationHistory(navigationHistory.slice(0, -1));
            setSelectedPage(previousPage);
        }
    };

    const expandToShowPage = async (page: KnowledgeBaseArticle) => {
        try {
            const ancestorIds = [...(page.ancestors?.map((a) => a.id) || []), page.id];
            await sidebarRef.current?.expandToPage(ancestorIds);
        } catch (err) {
            logger.error("Failed to expand tree for page:", err);
        }
    };

    if (loading) {
        return (
            <div className={styles.knowledgeBaseContainer}>
                <LoadingIndicator />
            </div>
        );
    }

    if (error) {
        return <ErrorMessage />;
    }

    return (
        <div className={styles.knowledgeBaseContainer}>
            <KnowledgeBaseSidebar
                ref={sidebarRef}
                onLogoClick={handleLogoClick}
                rootPages={rootPages}
                rootPageTitles={rootPageTitles}
                selectedLanguage={selectedLanguage}
                onLanguageChange={handleLanguageChange}
                selectedPage={selectedPage}
                onArticleClick={handleArticleClick}
            />
            <div className={styles.sidebarResizer} onMouseDown={() => sidebarRef.current?.startDragging()} />

            <div className={styles.mainContent}>
                {selectedPage ? (
                    <KnowledgeBasePageContent
                        selectedPage={selectedPage}
                        selectedLanguage={selectedLanguage}
                        onBackClick={handleBackClick}
                        onNavigate={handleNavigate}
                        onExpandToShowPage={expandToShowPage}
                    />
                ) : (
                    <KnowledgeBaseWelcome selectedLanguage={selectedLanguage} onArticleClick={handleArticleClick} />
                )}
            </div>
        </div>
    );
};

export default KnowledgeBase;
